import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { Input } from 'antd';
import type { ChangeEvent, KeyboardEvent } from 'react';

import { useDebouncedCallback } from '@/hooks/use-debounced-callback';

import styles from './search-bar.module.scss';

export type SearchBarProps = {
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  /** Debounced while typing; fires immediately on Enter and clear. */
  onSearch: (term: string) => void;
  debounceMs?: number;
  /** Fill the container instead of the default 300px width. */
  block?: boolean;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
};

export function SearchBar({
  value,
  defaultValue,
  placeholder = 'Search',
  onSearch,
  debounceMs = 300,
  block,
  disabled,
  className,
  'aria-label': ariaLabel = 'Search',
}: SearchBarProps) {
  const debounced = useDebouncedCallback(onSearch, debounceMs);

  const fireNow = (term: string) => {
    debounced.cancel();
    onSearch(term);
  };

  return (
    <Input
      type="search"
      role="searchbox"
      value={value}
      defaultValue={defaultValue}
      placeholder={placeholder}
      disabled={disabled}
      allowClear
      aria-label={ariaLabel}
      prefix={<MagnifyingGlassIcon size={16} aria-hidden className={styles.icon} />}
      className={[styles.search, block ? styles.block : undefined, className]
        .filter(Boolean)
        .join(' ')}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        // antd's clear button emits a synthetic click-typed change with value ''
        if (event.type === 'click' && event.target.value === '') fireNow('');
        else debounced(event.target.value);
      }}
      onPressEnter={(event: KeyboardEvent<HTMLInputElement>) =>
        fireNow((event.target as HTMLInputElement).value)
      }
    />
  );
}
